import { Languages } from "./../languages/services/exdr-language.service";
import { Injectable } from "@angular/core";
import { AuthService, AuthType } from "../shared/services/auth.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, concatMap, map, switchMap, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, of, Subject, throwError } from "rxjs";
import {
  BlogLink,
  Category,
  Document,
  PageableResponse,
  Product,
  Provider,
  SearchProduct,
  SearchUserParams,
  TopicType,
  Video,
  Webinar,
  Feedback,
  FeedbackBasic,
  LinkedCategory,
  SearchLinkedTopics,
  TechnadoptOrganizationInterface,
  MetaTopicTag,
} from "./interfaces";
import { PriceSlideContentModel, SlideModel } from "./models/slide.model";
import { ProductDetail } from "./models/product-detail.model";
import { TechnadoptUser } from "./models/technadopt-user.model";
import { TechnadoptImage } from "./models/technadopt-image.model";
import { ExdrLanguageService } from "../languages/services/exdr-language.service";
import { ProductStatus } from "./enums";
import { TechnadoptTokenResolverGuard } from "../shared/guards/technadopt-token-resolver-guard.service";
// import * as html2pdf from 'html2pdf.js';

@Injectable({
  providedIn: "root",
})
export class ProductsService {
  // Topic Status Constants
  static readonly TOPIC_STATUS = {
    DRAFT: 'Draft',
    PENDING: 'Pending',
    PUBLISHED: 'Published'
  } as const;

  refreshTagGroups$: Subject<any> = new Subject();
  refreshTopics$: Subject<any> = new Subject();
  refreshCategory$: Subject<any> = new Subject();
  // Signal emitted when the HTML/ZIP upload dialog completes an upload. Payload: { name, description, fileBase64, fileName, topicType }
  public htmlUploadSignal: Subject<any> = new Subject<any>();
  private galleryDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public galleryData$: Observable<any> = this.galleryDataSubject.asObservable();
  public topicTypeIds: BehaviorSubject<{ id: string }> = new BehaviorSubject<{ id: string }>({ id: '' });

  private filter$ = new BehaviorSubject({});
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private _exdrLangService: ExdrLanguageService,
    private tokenResolver: TechnadoptTokenResolverGuard
  ) { }


  refreshCode(id: string): void {
    this.topicTypeIds.next({ id });
  }



  setData(data: any): void {
    this.galleryDataSubject.next(data);
  }

  getData(): Observable<any> {
    return this.galleryData$;
  }

  getFilterObs(): Observable<any> {
    return this.filter$.asObservable();
  }

  setFilterObs(filter: any) {
    this.filter$.next(filter);
  }

  getProductDetail(productId: string): Observable<ProductDetail> {
    const timestamp = new Date().getTime(); // Prevent caching
    const url = `api/Category/GetCategoryDetailModel?CategoryID=${productId}&_=${timestamp}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    return this.http.get<ProductDetail>(url, options).pipe(
      map((val) => {
        return new ProductDetail(val);
      })
    );
  }
  getProductDetailBySlug(slug: string): Observable<ProductDetail> {
    const url = `api/Category/GetCategoryDetailModel?slug=${slug}`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    return this.http.get<ProductDetail>(url, options).pipe(
      map((val) => {
        return new ProductDetail(val);
      })
    );
  }

  /**
   * Id of the topic paired with `productDetails` for `locale` through the
   * "Different Topic for Other Language" pairing (TopicTranslations).
   *
   * Keyed on the paired topic ids (EN/AR), not the slugs: addCategoryLanguage posts
   * SlugEN/SlugAR as empty strings, so the slugs cannot be relied on.
   *
   * Returns null when no pairing exists, or when `productDetails` already is the
   * topic for `locale`.
   */
  getTranslatedTopicId(productDetails: ProductDetail, locale: string): string | null {
    if (locale !== 'en' && locale !== 'ar') {
      return null;
    }

    const normalize = (value?: string) => (value || '').trim();
    const translations = productDetails?.TopicTranslations;
    const topicEN = normalize(translations?.EN);
    const topicAR = normalize(translations?.AR);

    // Guard: no pairing configured, or both sides point at the same topic
    if (!topicEN || !topicAR || topicEN === topicAR) {
      return null;
    }

    const translatedId = locale === 'ar' ? topicAR : topicEN;

    // Guard: already showing the topic for this locale (prevents redirect loops)
    if (translatedId === normalize(productDetails?.CategoryID)) {
      return null;
    }

    return translatedId;
  }

  /** Slug of a topic, by id. Emits null when it cannot be determined. */
  getTopicSlugById(topicId: string): Observable<string | null> {
    return this.getProduct(topicId, false).pipe(
      map((product) => product?.Slug || null),
      catchError(() => of(null))
    );
  }

  moveItem(
    itemName: string,
    selfCategoryId: string,
    othersCategoryId: string,
    accessListIds: string = null
  ): Observable<any> {
    const url = `api/${itemName}/${selfCategoryId}/position`;
    let qp;
    if (itemName == "taggroup") {
      qp = {
        beforeTagGroupId: othersCategoryId,
        accessListIds: accessListIds,
      };
    } else {
      qp = {
        beforeCategoryId: othersCategoryId,
      };
    }
    let params: HttpParams = new HttpParams({
      fromObject: qp,
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.put(url, {}, options);
  }

  lockTopic(categoryId: string): Observable<any> {
    const url = `api/Category/${categoryId}/lock`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.put(url, {}, options);
  }

  // Delete topic
  deleteTopic(categoryId: string): Observable<any> {
    const url = `api/Category/${categoryId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.delete(url, options);
  }

  // Creates a duplicate of an existing topic
  cloneTopic(categoryId: string, cloneName: string) {
    const url = `api/Category/Clone`;
    const body = {
      CategoryId: categoryId,
      CloneName: cloneName
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post(url, body, options);
  }

  // Overwrites an existing topic
  overwriteTopic(sourceId: string, targetId: string) {
    const url = `api/Category/Overwrite`;
    const body = {
      SourceCategoryId: sourceId,
      TargetCategoryId: targetId
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post(url, body, options);
  }

  unlockTopic(categoryId: string): Observable<any> {
    const url = `api/Category/${categoryId}/lock`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.delete(url, options);
  }

  isTopicActive(categoryId: string, isActive: boolean): Observable<any> {
    const url = `api/Category/ActivateDeactivateCategory`;
    const body = {
      CategoryID: categoryId,
      IsActive: isActive,
    };

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.put(url, body, options);
  }

  /**
   * Update topic status (Draft, Pending, Published)
   * @param categoryId - The category/topic ID
   * @param status - The new status to set (Draft, Pending, Published)
   * @returns Observable<any>
   */
  updateTopicStatus(categoryId: string, status: string): Observable<any> {
    // Validate status
    const validStatuses = Object.values(ProductsService.TOPIC_STATUS);
    if (!validStatuses.includes(status as any)) {
      throw new Error(`Invalid status: ${status}. Valid statuses are: ${validStatuses.join(', ')}`);
    }

    // Only send data in the request body, no query parameters
    const url = `api/Category/UpdateTopicStatus`;
    const body = {
      CategoryId: categoryId,
      Status: status
    };

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json'),
    };

    return this.http.post(url, body, options);
  }

  /**
   * Get topic status for a specific category
   * @param categoryId - The category/topic ID
   * @returns Observable<{status: string}>
   */
  getTopicStatus(categoryId: string): Observable<{ status: string }> {
    const timestamp = new Date().getTime(); // Prevent caching
    const url = `api/Category/GetTopicStatus?CategoryId=${categoryId}&_=${timestamp}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    return this.http.get<any>(url, options)
      .pipe(
        map((value: string) => {
          return { status: value }
        })
      );

    // return this.getProductDetail(categoryId)
    // .pipe(
    //   map((value: ProductDetail) => {
    //     return { status: value?.TopicStatus };
    //   })
    // )
  }

  loadPricingDetails(categoryId: string): Observable<any> {
    const url = `/api/category/${categoryId}/offer`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.get<any[]>(url, options).pipe(map(res => {
      return res.map(p => {
        const price = new PriceSlideContentModel(p);
        return price;
      })
    }));
  }
  addPricing(categoryId: string, body): Observable<any> {
    const url = `/api/category/${categoryId}/offer?categorydId=${categoryId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<any[]>(url, body, options);
  }

  updatePricing(categoryId: string, offerId, body): Observable<any> {
    const url = `/api/category/${categoryId}/offer/${offerId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.put<any[]>(url, body, options);
  }

  deletePricing(categoryId: string, offerId): Observable<any> {
    const url = `/api/category/${categoryId}/offer/${offerId}?categorydId=${categoryId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.delete<any[]>(url, options);
  }

  getProductTypes(
    search?: {
      organziationId?: string;
    },
    accessList?: string,
    allowSealedTemplates: boolean = true
  ): Observable<TopicType[]> {
    const url = `api/Category/GetCategoryTypes`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        accessListIds: (search && search.organziationId) || accessList || "",
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };

    return this.http
      .get<{ TopicTypes: TopicType[] }>(url, options)
      .pipe(
        map((types) => {
          let topicTypes = types.TopicTypes;

          // Filter out sealed templates if not allowed
          if (!allowSealedTemplates) {
            topicTypes = topicTypes.filter(tt => !tt.SealedTemplate);
          }

          return topicTypes;
        })
      );
  }

  getUsersList(search: SearchUserParams): Observable<{
    Paging: PageableResponse[];
    CustomerUsers: TechnadoptUser[];
  }> {
    const url = `api/User/GetUserList`;
    const params: HttpParams = new HttpParams({
      fromObject: {
        sortBy: search.sortBy,
        startRowIndex: search.startRowIndex && search.startRowIndex + "",
        maximumRows: search.maximumRows && search.maximumRows + "",
        search: search.search,
        isActive: search.isActive + "",
        organizationIds: search.organizationIds.join(","),
        role: search.role || "",
      },
    });
    // params.keys().forEach(key => {
    //   const val = params.get(key);
    //   if (!val) {
    //     params = params.delete(key);
    //   }
    // });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };

    return this.http.get<{
      Paging: PageableResponse[];
      CustomerUsers: TechnadoptUser[];
    }>(url, options);
  }

  isExistingProduct(productId: string, productName: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const body = {
      CategoryID: productId,
      CategoryName: productName,
    };

    return this.http.post(`api/Category/IsExistingCategory`, body, options);
  }

  updateProduct(productDetail: ProductDetail) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const body = {
      CategoryID: productDetail.CategoryID,
      CategoryName: productDetail.CategoryName,
      Description: productDetail.Description,
      Image: productDetail.Image,
      OwnerDesignation: productDetail.OwnerDesignation,
      OwnerIdenedi: productDetail.OwnerIdenedi,
      TopicOwnerUserId: productDetail.OwnerIdenedi
        ? null
        : productDetail.TopicOwnerUserId,
      TopicTypeID: productDetail.TopicTypeID,
      VideoURL: productDetail.VideoURL,
      Website: productDetail.Website,
      YoutubeChannelID: productDetail.YoutubeChannelID,
      OpenForOwnership: productDetail.OpenForOwnership,
      CoverImageURL: productDetail.CoverImageURL,
      MetaDescription: productDetail.MetaDescription,
      MetaTitle: productDetail.MetaTitle,
      MetaTopicTags: productDetail.MetaTopicTags,
      Languages: productDetail.Languages,
      RenderType: productDetail?.RenderType
    };

    return this.http.put(`api/Category/Update`, body, options);
  }

  createProduct(productDetail: ProductDetail, accessList: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const body = {
      AccessListId: accessList,
      CategoryID: null,
      CategoryName: productDetail.CategoryName,
      Description: productDetail.Description,
      Image: productDetail.Image,
      OwnerDesignation: productDetail.OwnerDesignation,
      OwnerIdenedi: productDetail.OwnerIdenedi,
      TopicOwnerUserId: productDetail.OwnerIdenedi
        ? null
        : productDetail.TopicOwnerUserId,
      TopicTypeID: productDetail.TopicTypeID,
      VideoURL: productDetail.VideoURL,
      Website: productDetail.Website,
      YoutubeChannelID: productDetail.YoutubeChannelID,
      OpenForOwnership: productDetail.OpenForOwnership,
      CoverImageURL: productDetail.CoverImageURL,
      MetaTitle: productDetail.MetaTitle,
      MetaDescription: productDetail.MetaDescription,
      TopicTags: productDetail.MetaTopicTags,
      Languages: productDetail.Languages,
      RenderType: productDetail?.RenderType
    };

    return this.http.post(`api/Category/Add`, body, options);
  }

  addHtmlPage(
    accessListId: string,
    categoryName: string,
    categoryDescription: string,
    slideContent: object | string
  ): Observable<any> {
    const url = `api/Category/AddHtmlPage`;
    const options = {
      headers: this.authService
        .buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json'),
    };

    const body: any = {
      AccesslistId: accessListId,
      CategoryName: categoryName,
      CategoryDescription: categoryDescription,
      SlideContent:
        typeof slideContent === 'string' ? slideContent : JSON.stringify(slideContent),
    };

    return this.http.post(url, body, options);
  }

  updateHtmlPage(
    CategoryId: string,
    SlideId: string,
    TopicTypeId: string,
    categoryName: string,
    categoryDescription: string,
    slideContent: object | string
  ): Observable<any> {
    const url = `api/Category/UpdateHtmlPage`;
    const options = {
      headers: this.authService
        .buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json'),
    };

    const body: any = {
      CategoryId: CategoryId,
      SlideId: SlideId,
      TopicTypeId: TopicTypeId,
      CategoryName: categoryName,
      CategoryDescription: categoryDescription,
      SlideContent:
        typeof slideContent === 'string' ? slideContent : JSON.stringify(slideContent),
    };

    return this.http.post(url, body, options);
  }

  getHTMLPageContent(CategoryId: string): Observable<Array<string>> {
    const url = `/api/Category/GetHtmlPage`;
    const timestamp = new Date().getTime();
    let params = new HttpParams({
      fromObject: {
        CategoryId: CategoryId,
        timestamp: timestamp.toString()
      }
    })
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<Array<string>>(url, options)
  }

  addCategoryLanguage(EN: string, AR: string) {
    const url = `api/Category/Translations`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const body = {
      EN: EN,
      AR: AR,
      SlugEN: '',
      SlugAR: '',
    };
    return this.http.post(url, body, options);
  }

  getMineTab(accessListIds: string = ''): Observable<Array<string>> {
    const url = `/api/Category/Id`;
    let params = new HttpParams({
      fromObject: {
        editableOnly: true,
        filterAccessListIds: accessListIds
      }
    })
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<Array<string>>(url, options)
  }

  getProducts(
    search?: SearchProduct,
    isRelatedTopicsCall: boolean = false
  ): Observable<{ Paging: PageableResponse[]; Topics: Product[] }> {
    if (isRelatedTopicsCall) {
      const url = `/api/Category/GetAll`;
      let params: HttpParams = new HttpParams({
        fromObject: {
          startRowIndex: search.start && search.start + "",
          maximumRows: search.limit && search.limit + "",
          search: search.searchTerm ?? '',
          isAdminCall: search.isAdminCall == false ? "false" : "true",
          categoryId: search.topicTypeId ?? '',
          accessListIds: search.organziationId ?? '',
        },
      });
      params.keys().forEach((key) => {
        const val = params.get(key);
        if (!val) {
          params = params.delete(key);
        }
      });
      const options = {
        headers: this.authService.buildAuthHeader(AuthType.Technadopt),
        params,
      };

      return this.http.get<any>(url, options).pipe(
        map((res) => {
          let products: Product[] = [];
          res?.Categories.forEach((cat) => {
            let topic: any = {
              TopicId: cat?.CategoryID,
              TopicName: cat?.CategoryName,
              TopicDescription: cat?.Description,
              ImageUrl: cat?.ImageURL,
              TopicTags: cat?.TopicTags,
              Locked: cat?.Locked,
              IsActive: cat?.IsActive,
            };
            products.push(topic);
          });

          return { Paging: res.Paging, Topics: products };
        })
      );
    } else {
      const url = `/api/exceeders/topics`;
      let params: HttpParams = new HttpParams({
        fromObject: {
          start: search.start && search.start + "",
          limit: search.limit && search.limit + "",
          search: search.searchTerm ?? '',
          filterTags: search.tags?.join(",") || "",
          filterCategories: search.categories?.join(",") || "",
          accessListIds: search.organziationId || "",
          activeOnly: search.activeOnly == false ? "false" : "true",
          isAdminCall: search.isAdminCall == false ? "false" : "true",
          TopicTypeId: search.topicTypeId || search.filterTopicTypeId || "",
          Language: search.language ?? '',
        },
      });
      params.keys().forEach((key) => {
        const val = params.get(key);
        if (!val) {
          params = params.delete(key);
        }
      });
      const options = {
        headers: this.authService.buildAuthHeader(AuthType.Technadopt),
        params,
      };

      return this.http
        .get<{ Paging: PageableResponse[]; Topics: Product[] }>(url, options)
        .pipe(
          map((res: { Paging: PageableResponse[]; Topics: Product[] }) => {
            res.Topics.forEach((t) => {
              t.Status = ProductStatus.Visible;
              if (t.Locked) {
                t.Status = ProductStatus.Lock;
              }
              if (!t.IsActive) {
                t.Status = ProductStatus.Hide;
              }
            });
            return res;
          })
        );
    }
  }

  getMyTopics(search?: SearchProduct,) {
    if (!this.authService.isLoggedIn()) {
      throw Error('User not Logged In')
    }
    return this.tokenResolver.resolveToken().pipe(concatMap(({ addon, userAccessListIds }) => {
      if (!addon) {
        throw Error('Technadopt Not Connected')
      }
      search.organziationId = userAccessListIds ?? 'SOME_RANDOM_ACCESS_LIST_TO_PREVENT_PUBLIC_DATA_ACCESS'
      search.isAdminCall = false
      return this.getProducts(search);
    }))
  }
  getGroupProducts(
    groupId: string,
    search: SearchProduct
  ): Observable<{ Paging: PageableResponse[]; Topics: Product[] }> {
    return this.authService
      .getPublicGroupAddon(groupId, AuthType.Technadopt)
      .pipe(
        switchMap((addon) => {
          const url = `${addon.BaseUrl}/api/exceeders/topics`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start && search.start + "",
              limit: search.limit && search.limit + "",
              search: search.searchTerm,
              filterTags: search.tags?.join(",") || "",
              filterCategories: search.categories?.join(",") || "",
              accessListIds: search.organziationId || "",
              activeOnly: "false",
            },
          });
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });
          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };
          return this.http
            .get<{ Paging: PageableResponse[]; Topics: Product[] }>(
              url,
              options
            )
            .pipe(
              map((res: { Paging: PageableResponse[]; Topics: Product[] }) => {
                res.Topics.forEach((t) => {
                  if (t.IsActive) {
                    t.Status = ProductStatus.Visible;
                  } else if (t.Locked) {
                    t.Status = ProductStatus.Lock;
                  } else {
                    t.Status = ProductStatus.Hide;
                  }
                });
                return res;
              })
            );
        })
      );
  }

  getProduct(
    productId: string,
    activeOnly: boolean = true
  ): Observable<Product> {
    return this.getProducts({ searchTerm: productId, activeOnly }).pipe(
      map((response) => {
        return response.Topics[0];
      })
    );
  }

  getGroupProduct(groupId: string, productId: string): Observable<Product> {
    return this.getGroupProducts(groupId, { searchTerm: productId }).pipe(
      map((response) => {
        return response.Topics[0];
      })
    );
  }

  getCategories(accessListIds: string, topicTypeIds?: string): Observable<Category[]> {
    const url = `/api/exceeders/topic-categories`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        accessListIds: accessListIds || "",
        topicTypeIds: topicTypeIds || ""
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };

    return this.http.get<Category[]>(url, options);
  }

  getProviders(search: {
    filterCategoryIds?: string;
    accessListIds?: string;
  }): Observable<Provider[]> {
    const url = `/api/exceeders/topic-tags`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        accessListIds: search.accessListIds || "",
        filterCategoryIds: search.filterCategoryIds || "",
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };

    return this.http.get<Provider[]>(url, options);
  }

  getLinkedTopics(search: SearchLinkedTopics): Observable<{
    Paging: PageableResponse;
    LinkedCategories: LinkedCategory[];
  }> {
    const {
      categoryId,
      accessListIds,
      sortBy,
      startRowIndex,
      maximumRows,
      search: searchTerm,
    } = search;
    let params: HttpParams = new HttpParams({
      fromObject: {
        categoryId: categoryId ?? '',
        accessListIds: accessListIds ?? '',
        sortBy: sortBy ?? '',
        startRowIndex: startRowIndex && startRowIndex + "",
        maximumRows: maximumRows && maximumRows + "",
        search: searchTerm ?? '',
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<{
      Paging: PageableResponse;
      LinkedCategories: LinkedCategory[];
    }>(`api/Category/Link/GetAll`, options);
  }

  // PRODUCT-MATERIALS-DETAILS

  getVideos(
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse; Videos: Video[] }> {
    const url = `/api/exceeders/topic-videos/${topicId}`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        start: search.start + "",
        limit: search.limit + "",
        search: search.searchTerm ?? '',
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<{ Paging: PageableResponse; Videos: Video[] }>(
      url,
      options
    );
  }

  getGroupVideos(
    groupId: string,
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse; Videos: Video[] }> {
    return this.authService
      .getPublicGroupAddon(groupId, AuthType.Technadopt)
      .pipe(
        switchMap((addon) => {
          const url = `${addon.BaseUrl}/api/exceeders/topic-videos/${topicId}`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start + "",
              limit: search.limit + "",
              search: search.searchTerm ?? '',
            },
          });
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });
          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };
          return this.http.get<{ Paging: PageableResponse; Videos: Video[] }>(
            url,
            options
          );
        })
      );
  }

  getDocuments(
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
      tags?: string[];
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
        tags: []
      }
  ): Observable<{ Paging: PageableResponse[]; Documents: Document[] }> {
    const url = `/api/exceeders/topic-documents/${topicId}`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        start: search.start + "",
        limit: search.limit + "",
        search: search.searchTerm ?? '',
        filterTags: search.tags?.join(",") || "",
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<{ Paging: PageableResponse[]; Documents: Document[] }>(
      url,
      options
    );
  }

  getGroupDocuments(
    groupId: string,
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
      tags?: string[];
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
        tags: []
      }
  ): Observable<{ Paging: PageableResponse[]; Documents: Document[] }> {
    return this.authService
      .getPublicGroupAddon(groupId, AuthType.Technadopt)
      .pipe(
        switchMap((addon) => {
          const url = `${addon.BaseUrl}/api/exceeders/topic-documents/${topicId}`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start + "",
              limit: search.limit + "",
              search: search.searchTerm ?? '',
              filterTags: search.tags?.join(",") || "",
            },
          });
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });
          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };
          return this.http.get<{
            Paging: PageableResponse[];
            Documents: Document[];
          }>(url, options);
        })
      );
  }

  getBlogLinks(
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse[]; Blogs: BlogLink[] }> {
    const url = `/api/exceeders/topic-blogs/${topicId}`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        start: search.start + "",
        limit: search.limit + "",
        search: search.searchTerm ?? '',
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<{ Paging: PageableResponse[]; Blogs: BlogLink[] }>(
      url,
      options
    );
  }

  getGroupBlogLinks(
    groupId: string,
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse[]; Blogs: BlogLink[] }> {
    return this.authService
      .getPublicGroupAddon(groupId, AuthType.Technadopt)
      .pipe(
        switchMap((addon) => {
          const url = `${addon.BaseUrl}/api/exceeders/topic-blogs/${topicId}`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start + "",
              limit: search.limit + "",
              search: search.searchTerm ?? '',
            },
          });
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });
          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };
          return this.http.get<{
            Paging: PageableResponse[];
            Blogs: BlogLink[];
          }>(url, options);
        })
      );
  }

  getWebinars(
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse[]; Webinars: Webinar[] }> {
    const url = `/api/exceeders/topic-webinars/${topicId}`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        start: search.start + "",
        limit: search.limit + "",
        search: search.searchTerm ?? '',
      },
    });
    params.keys().forEach((key) => {
      const val = params.get(key);
      if (!val) {
        params = params.delete(key);
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params,
    };
    return this.http.get<{ Paging: PageableResponse[]; Webinars: Webinar[] }>(
      url,
      options
    );
  }

  getGroupWebinars(
    groupId: string,
    topicId: string,
    search: {
      start?: number;
      limit?: number;
      searchTerm?: string;
    } = {
        start: 0,
        limit: 10,
        searchTerm: "",
      }
  ): Observable<{ Paging: PageableResponse[]; Webinars: Webinar[] }> {
    return this.authService
      .getPublicGroupAddon(groupId, AuthType.Technadopt)
      .pipe(
        switchMap((addon) => {
          const url = `${addon.BaseUrl}/api/exceeders/topic-webinars/${topicId}`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start + "",
              limit: search.limit + "",
              search: search.searchTerm ?? '',
            },
          });
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });
          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };
          return this.http.get<{
            Paging: PageableResponse[];
            Webinars: Webinar[];
          }>(url, options);
        })
      );
  }


  getMaterialsCount(productId) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http
      .get<any>(
        `api/exceeders/topic-channels-count/${productId}`,
        options
      );
  }

  getSlidesV2(productId: string): Observable<SlideModel[]> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http
      .get<SlideModel[]>(`api/Category/${productId}/Slide`, options)
      .pipe(
        map(
          (res) =>
            res
              .map((slide) => new SlideModel(slide))
              .sort((a, b) => a.Order - b.Order) || []
        )
      );
  }

  uploadTechnadoptImage(fileName: string, fileBase64Data: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const body = {
      FileName: fileName,
      File: fileBase64Data,
    };
    return this.http
      .post(`api/Attachment/UploadImageFromBytes`, body, options)
      .pipe(
        map((val: any) => {
          return new TechnadoptImage(val);
        })
      );
  }
  uploadTechnadoptZip(accessListId: string, file: File) {
    const headers = this.authService.buildAuthHeader(AuthType.Technadopt);
    const url = `api/attachment/upload-zip/${accessListId}`;

    const formData = new FormData();
    formData.append('', file, file.name);

    return this.http.post(url, formData, { headers }).pipe(
      map((res: any) => res)
    );
  }


  // FEEDBACK
  getCustomerFeedback(
    topicId: string,
    startRowIndex: number,
    maximumRows: number
  ): Observable<Feedback[]> {
    const url = `api/Category/Rating/GetAll?categoryId=${topicId}&startRowIndex=${startRowIndex}&maximumRows=${maximumRows}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http
      .get<{ Paging: PageableResponse[]; Ratings: Feedback[] }>(url, options)
      .pipe(map((res) => res.Ratings));
  }

  getGetMineFeedback(topicId: string): Observable<FeedbackBasic> {
    const url = `api/Category/Rating/GetMine?categoryId=${topicId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.get<FeedbackBasic>(url, options);
  }

  sendFeedback(data: {
    CategoryId: string;
    UserId: string;
    Rating: string;
    Comment: string;
  }): Observable<string> {
    const url = "api/Category/Rating";
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(url, { ...data }, options);
  }

  getMetaDataTags(): Observable<MetaTopicTag[]> {
    const url = `api/o365/GetMetadataTopicTags`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.get<MetaTopicTag[]>(url, options);
  }

  getTagGroups(
    accessListIds: string,
    language: string,
    usedOnly = false,
    filterCategoryIds?: string,
    filterTagIds?: string,
    topicTypeId?: string
  ) {
    const url = "api/taggroup";

    const payload: any = {
      language,
      accessListIds,
    };
    if (usedOnly) {
      payload.usedOnly = usedOnly;
    }
    if (filterCategoryIds) {
      payload.filterCategoryIds = filterCategoryIds;
    }
    if (filterTagIds) {
      payload.filterTagIds = filterTagIds;
    }
    if (topicTypeId) {
      payload.topicTypeIds = topicTypeId;
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params: new HttpParams({
        fromObject: payload,
      }),
    };
    return this.http.get<any[]>(url, options);
  }


  getTopicCategories(
    accessListIds: string,
    startRowIndex: number,
    search: '',
    categoryId: string,
  ) {

    const url = "api/o365/GetTopicCategories";

    const payload: any = {
      accessListIds,
      startRowIndex,
      search: '',
      categoryId,
    };

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params: new HttpParams({
        fromObject: payload,
      }),
    };
    return this.http.get<any[]>(url, options);
  }

  addTopicCategories(TopicCategoryName: string, topicId?: string, topicCategory?: string) {

    const url = "api/o365/AddTopicCategory";

    const payload: any = {
      TopicCategoryName,
    };

    if (topicId) {
      payload.CategoryId = topicId;
    } else {
      payload.TopicCategoryId = topicCategory;
    }

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<any[]>(url, payload, options);


  }

  removeTopicCategories(TopicCategoryId: string,) {

    const url = "api/o365/RemoveTopicCategory";


    const payload: any = {
      TopicCategoryId,
    };
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<any[]>(url, payload, options);


  }


  validateYoutubePlaylist(ChannelId): Observable<any[]> {
    let apiKey = "AIzaSyB8nT29XveXpOUgPrxxozHosTxhV_Rb0io";
    const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=id&playlistId=${ChannelId}&key=${apiKey}`;

    return this.http.get<any[]>(url);
  }

  saveTopicTag(tagGroupId, tagName, tagId?, topicId?) {
    const url = "api/o365/AddUpdateTopicTag";
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      TagName: tagName,
      TagGroupId: tagGroupId,
    };
    if (tagId) {
      payload.TopicTagId = tagId;
    }
    if (topicId) {
      payload.CategoryId = topicId;
    }
    return this.http.post<string>(url, payload, options);
  }
  deleteTopicTag(tagId) {
    const url = "api/o365/RemoveTopicTag";
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      TopicTagId: tagId,
    };
    return this.http.post<string>(url, payload, options);
  }
  createTagGroup(tagName, accessListId, language, type = 0) {
    const url = "api/taggroup";
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      Name: tagName,
      Language: language,
      Type: type,
      AccessListId: accessListId,
    };
    return this.http.post<string>(url, payload, options);
  }
  createTagGroupWithType(tagName, accessListId, language, type) {
    const url = "api/taggroup";
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      Name: tagName,
      Language: language,
      Type: type,
      AccessListId: accessListId,
    };
    return this.http.post<string>(url, payload, options);
  }


  createBlogLinks(topicId, topicLink) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      categoryId: topicId,
      url: topicLink,
      Type: 'WebLink'
    };
    return this.http.post<string>(
      `api/Library/CreateLibraryLink`,
      payload,
      options
    );
  }

  createVideoLinks(topicId, topicLink, type) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      CategoryId: topicId,
      Url: topicLink,
      Type: type
    };
    return this.http.post<string>(
      `api/Library/CreateLibraryLink`,
      payload,
      options
    );
  }

  uploadDocument(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Attachment/UploadMediaToLocalLibrary`,
      payload,
      options
    );
  }

  updateDocument(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    // payload = {
    //   "Id": payload.documentId,
    //   "Description": payload.description,
    //   "LibraryDriveTagIds": payload.LibraryDriveTagIds
    // }
    return this.http.post<string>(
      `api/Library/UpdateLibraryDrive`,
      payload,
      options
    );
  }

  deleteDocument(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Attachment/r/${payload}`,
      payload,
      options
    );
  }

  deleteBlogLink(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Library/DeleteLibraryLink?LibraryLinkId=${payload}`,
      payload,
      options
    );
  }

  deleteChannel(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Category/RemoveChannelWithCategory?categoryChannelId=${payload}`,
      payload,
      options
    );
  }

  addVimeoChannel(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Category/AssociateVimeoChannel`,
      payload,
      options
    );
  }

  addYoutubeChannel(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Category/AssociateYouTubePlaylist`,
      payload,
      options
    );
  }

  addYoutubePlaylist(payload) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.post<string>(
      `api/Category/AssociateYouTubePlaylist`,
      payload,
      options
    );
  }

  getPlayList(categoryId, search) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params: new HttpParams({
        fromObject: {
          startRowIndex: search.start,
          maximumRows: search.limit,
          search: search.searchTerm,
        },
      }),
    };
    return this.http.get<string>(
      `api/Category/GetCategoryChannels?categoryId=${categoryId}`,
      options
    );
  }

  updateTagGroup(tagGroupId, tagName, accessListId, language, type = 0) {
    const url = `api/taggroup/${tagGroupId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      Name: tagName,
      Language: language,
      Type: type,
      AccessListId: accessListId,
    };
    return this.http.put<string>(url, payload, options);
  }

  updateTagGroupWithType(tagGroupId, tagName, accessListId, language, type) {
    const url = `api/taggroup/${tagGroupId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      Name: tagName,
      Language: language,
      Type: type,
      AccessListId: accessListId,
    };
    return this.http.put<string>(url, payload, options);
  }
  deleteTagGroup(tagGroupId) {
    const url = `api/taggroup/${tagGroupId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    return this.http.delete<string>(url, options);
  }

  addRemoveTopicTag(topicId, tagId, deactivate: boolean = false) {
    const url = `api/o365/AddRemoveTopicTagMapper`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      CategoryId: topicId,
      TopicTagId: tagId,
    };
    if (deactivate) {
      payload.Deactivate = true;
    }
    return this.http.post<string>(url, payload, options);
  }

  addRemoveTopicCategory(topicId, catId) {
    const url = `api/o365/AddRemoveTopicCategoryMapper`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };
    const payload: any = {
      TopicCategoryId: topicId,
      CategoryId: catId
    };
    return this.http.post<string>(url, payload, options);
  }

  getAllRelatedTopic(
    categoryId: string,
    start: number = 0,
    maximumRows: number = 10,
    search: string = ""
  ) {
    const url = `api/Category/Link/GetAll?startRowIndex=${start}&maximumRows=${maximumRows}&search=${search}&categoryId=${categoryId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    return this.http.get(url, options);
  }

  addToRelatedTopic(categoryId, linkedCategoryId) {
    const url = `api/Category/Link/Add`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    let payLoad = {
      CategoryId: categoryId,
      LinkedCategoryId: linkedCategoryId,
    };
    return this.http.post(url, payLoad, options);
  }

  removeFromRelatedTopic(linkId) {
    const url = `api/Category/Link/Remove`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    let payLoad = {
      CategoryLinkID: linkId,
    };
    return this.http.post(url, payLoad, options);
  }


  getCategoryDetail(model) {
    // https://technadoptqaui.azurewebsites.net/webapi/api/Category/GetCategoryDetailModel?&slug={Slug}
    const url = `api/Category/GetCategoryDetailModel?&slug=${model}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    };

    return this.http.get(url, options);
  }




  // pdf generated working
  // Method to initiate PDF generation
  printData(sectionId: string, productDetails: any): void {
    // const element = document.getElementById(sectionId);
    const element = document.getElementById(sectionId);
    if (!element) {
      console.error('Section not found');
      return;
    }

    // Find all images within the section and convert them to Base64
    const images = element.querySelectorAll('img');
    // .map((img) => this.convertBlobImageToBase64(img))
    const imagePromises = Array.from(images);

    const innerHtml = element?.innerHTML;

    // Once all images are converted, generate the PDF
    Promise.all(imagePromises)
      .then(() => {
        this.createConditionalPDF(element, productDetails);
      })
      .catch((error) => {
        console.error('Error converting images to Base64:', error);
      });
  }

  // Function to convert Blob image to Base64
  convertBlobImageToBase64(img: HTMLImageElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const imgURL = img.src; // Get the image URL
      fetch(imgURL)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            img.src = reader.result as string; // Replace the image src with the Base64 string
            resolve(); // Resolve the promise once conversion is complete
          };
          reader.onerror = () => reject('Error reading the Blob data.');
          reader.readAsDataURL(blob); // Convert blob to Base64
        })
        .catch(error => reject(`Error fetching the image Blob: ${error}`));
    });
  }

  // Function to create the PDF conditionally based on renderType
  createConditionalPDF(element: HTMLElement, productDetails: any): void {
    let options: any;

    if (productDetails?.RenderType === 'Connected' || productDetails?.RenderType == 'FullWidthConnectedSlides') {
      const elementWidthInPx = element.scrollWidth; // Get the width of the content in pixels
      const elementHeightInPx = element.scrollHeight; // Get the height of the content in pixels
      const pxToPtRatio = 0.75; // Conversion ratio from pixels to points (0.063rem = 0.75pt)
      const pdfWidth = elementWidthInPx * pxToPtRatio; // Convert content width to points
      const pdfHeight = elementHeightInPx * pxToPtRatio; // Convert content height to points

      const options = {
        margin: [0, 15, 0, 15], // Equal margins on all sides (top, left, bottom, right) in points
        filename: productDetails.CategoryName,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 2, useCORS: true, logging: true }, // Increase the scale for better quality
        jsPDF: { unit: 'pt', format: [pdfWidth, pdfHeight], orientation: 'portrait' }, // Custom page size
        pagebreak: { mode: [] } // Ensure no page breaks are used
      };

      // Generate and save the PDF using the customized page size and consistent margins
      // html2pdf().from(element).set(options).save();

    } else {
      // Multi-page layout with page breaks for each section
      options = {
        margin: 0.1,
        filename: productDetails.CategoryName,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 3, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } // Enable page breaks for Default renderType
      };

      // Generate and save the PDF using html2pdf.js
      // html2pdf()
      //   .from(element)
      //   .set(options)
      //   .toPdf()
      //   .get('pdf')
      //   .then((pdf) => {
      //     // Adjust the PDF pages based on content height for dynamic page sizing
      //     const totalPages = pdf.internal.getNumberOfPages();
      //     for (let i = 1; i <= totalPages; i++) {
      //       pdf.setPage(i);
      //       const pageHeight = pdf.internal.pageSize.getHeight();
      //       const sectionHeight = pdf.internal.pageSize.height;
      //       pdf.internal.pageSize.height = sectionHeight < pageHeight ? sectionHeight : pageHeight;
      //     }
      //   })
      //   .save();
    }
  }

  filterVisibleSlides(slides: SlideModel[]): SlideModel[] {


    if (slides.length === 0) return [];

    return slides.filter(item =>
      item.IsActive &&
      (
        (!item.SlideContent?.hasOwnProperty('ViewOn')) ||
        item.SlideContent.ViewOn?.includes('Web')
      )
    );
  }

  parseSlideTitle(slides: SlideModel[], userProfile: any): SlideModel[] {
    if (slides?.length === 0) return

    if (!userProfile) return slides; // Return original slides if no user profile

    const placeholders = {
      '{{MemberProfile\\Full Name}}': `${userProfile.FirstName || ''} ${userProfile.LastName || ''}`.trim(),
      '{{MemberProfile\\First Name}}': `${userProfile.FirstName || ''}`.trim(),
      // '{{MemberProfile\\Department}}': '',
      // '{{MemberProfile\\Country}}': '',
    };

    return slides.map(slide => {
      const content = slide.SlideContent;

      if (!content) return slide; // Skip if SlideContent is missing

      const replaceInText = (text: string) => {
        if (!text) return text;
        let result = text;
        Object.entries(placeholders).forEach(([placeholder, value]) => {
          const regex = new RegExp(placeholder.replace(/\\/g, '\\\\'), 'g');
          result = result.replace(regex, value);
        });
        return result;
      };

      const updatedContent = {
        ...content,
        Title: replaceInText(content.Title),
        Text: replaceInText(content.Text),
        SubTitle: replaceInText(content.SubTitle),
      };

      return {
        ...slide,
        SlideContent: updatedContent,
      };
    });
  }

  // Topic Approval Settings API methods
  getTopicApprovalSettings(accessListId: string): Observable<any> {
    const url = `/api/topicapprovalsettings?accessListId=${accessListId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json')
    };
    return this.http.get(url, options);
  }

  createTopicApprovalSettings(payload: any): Observable<any> {
    const url = `/api/topicapprovalsettings`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json')
    };
    return this.http.post(url, payload, options);
  }

  updateTopicApprovalSettings(settingsId: string, payload: any): Observable<any> {
    const url = `/api/topicapprovalsettings/${settingsId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json')
    };
    return this.http.put(url, payload, options);
  }


  fetchLibraryTags(startRowIndex: number = 0, maximumRows: number = 100, search: string = '', accessListIds: string = '', topicId: string = ''): Observable<{ Paging: PageableResponse[]; LibraryDriveTags: any[] }> {
    const url = `/api/library/tags`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        startRowIndex: startRowIndex.toString(),
        maximumRows: maximumRows.toString(),
        search: search || '',
        accessListId: accessListIds || '',
        topicId: topicId || '',
        timestamp: new Date().getTime().toString()
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      params
    };
    return this.http.get<{ Paging: PageableResponse[]; LibraryDriveTags: any[] }>(url, options);
  }

  addLibraryTags(newTags: { TagName: string }[], accessListId: string): Observable<string[]> {
    const url = `/api/library/tags?accesslistId=${accessListId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
    };
    // API expects array of tags, each with TagName
    return this.http.post<{ LibraryDriveTagId: string }[]>(url, newTags, options).pipe(
      map((response: any) => {
        // If response is array of objects with LibraryDriveTagId, extract IDs
        if (Array.isArray(response)) {
          return response.map((tag: any) => tag.LibraryDriveTagId || tag);
        }
        // If response is array of strings (IDs), return as is
        return [response];
      })
    );
  }

  createSealedTopic(topicData: any): Observable<any> {
    const checkUrl = 'api/Category/IsExistingCategory';
    const createUrl = 'api/Category/Add';
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt)
        .append('Content-Type', 'application/json')
        .append('Accept', 'application/json')
    };

    // First check if category exists
    return this.http.post(checkUrl, { CategoryName: topicData.CategoryName }, options).pipe(
      switchMap((response: any) => {
        // If response is false, category doesn't exist, so create it
        if (response === false) {
          return this.http.post(createUrl, topicData, options);
        } else {
          // Category already exists, throw error
          return throwError({ error: { ErrorMessage: 'Topic with this name already exists' } });
        }
      }),
      switchMap((createResponse: any) => {
        // After successful creation, update topic status to Published
        // const categoryId = createResponse;
        // if (categoryId) {
        //   return this.updateTopicStatus(categoryId, ProductsService.TOPIC_STATUS.PUBLISHED).pipe(
        //     map(() => createResponse) // Return original response after status update
        //   );
        // }
        return of(createResponse);
      })
    );
  }

  // isTopicActive(categoryId: string, isActive: boolean): Observable<any> {
  //   const url = `api/Category/ActivateDeactivateCategory`;
  //   const body = {
  //     CategoryID: categoryId,
  //     IsActive: isActive,
  //   };

  //   const options = {
  //     headers: this.authService.buildAuthHeader(AuthType.Technadopt),
  //   };
  //   return this.http.put(url, body, options);
  // }


  downloadHTMLTopic(SiteBaseUrl: string, FileName: string) {
    const url = 'api/Attachment/download-zip';
    const body = {
      SiteBaseUrl,
      FileName
    }
    // const options = {
    //   headers: this.authService.buildAuthHeader(AuthType.Technadopt),
    //   responseType: 'blob'
    // }
    return this.http.post(url, body, {
      headers: this.authService.buildAuthHeader(AuthType.Technadopt),
      responseType: 'blob'
    });
  }
}
